import { useEffect, useState } from "react";
import { CircleAlert, PanelLeftClose, PanelLeftOpen, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import Spinner from "@/components/Spinner";
import { BricksCanvas } from "./fragments/BricksCanvas";
import Menu from "./fragments/Menu";
import { useAppSelector, useAppDispatch } from "./store/hooks";
import { selectBricksLoading, selectBricksError, fetchBricks } from "./store/slices/bricksSlice";
import { selectDrawing, selectRenderError } from "./store/slices/wallManagerSlice";
import { prepareSelectedSeries, selectMissingImages } from "./store/slices/configurationSlice";
import Logo from "./assets/Ploegsteert-RGB-lang.png";

const App: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(true);
  const dispatch = useAppDispatch();
  const bricksLoading = useAppSelector(selectBricksLoading);
  const bricksError = useAppSelector(selectBricksError);
  const renderError = useAppSelector(selectRenderError);
  const error = bricksError ?? renderError;
  const missingImages = useAppSelector(selectMissingImages);
  const wallManagerDrawing = useAppSelector(selectDrawing);

  useEffect(() => {
    dispatch(fetchBricks()).then(() => dispatch(prepareSelectedSeries()));
  }, [dispatch]);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-canvas">
      <header className="z-10 flex h-14 shrink-0 items-center gap-3 border-b bg-background px-3 shadow-xs">
        <img src={Logo} alt="Ploegsteert" className="h-8 w-auto" />
        <Button
          variant={menuOpen ? "secondary" : "ghost"}
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="wall-menu"
        >
          {menuOpen ? <PanelLeftClose /> : <PanelLeftOpen />}
          Configureren
        </Button>
        {(wallManagerDrawing || bricksLoading) && (
          <span className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner />
            {bricksLoading ? "Stenen laden…" : "Muur tekenen…"}
          </span>
        )}
      </header>
      <div className="relative min-h-0 flex-1">
        <BricksCanvas />
        {(error || missingImages.length > 0) && (
          <div className="pointer-events-none absolute top-3 right-3 left-3 flex justify-end">
            {error ? (
              <Alert
                variant="destructive"
                className="pointer-events-auto max-w-md bg-background shadow-md"
              >
                <CircleAlert />
                <AlertTitle>
                  {bricksError
                    ? "De stenen konden niet geladen worden."
                    : "De muur kon niet getekend worden."}
                </AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <Alert
                variant="warning"
                className="pointer-events-auto max-w-md bg-background shadow-md"
              >
                <TriangleAlert />
                <AlertTitle>Niet alle stenen konden geladen worden.</AlertTitle>
                <AlertDescription>
                  {missingImages.length}{" "}
                  {missingImages.length === 1 ? "afbeelding wordt" : "afbeeldingen worden"}{" "}
                  overgeslagen: {missingImages.join(", ")}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
        {menuOpen && <Menu />}
      </div>
    </div>
  );
};

export default App;
